/**
 * Phase 5B: drawdown chart — derived client-side from the equity curve so we
 * don't round-trip to the backend. Recharts AreaChart with red fill.
 *
 * Denominator note: the backend's `equity_curve.equity` is a cumulative-rupee
 * running sum of `net_pnl` (see `_aggregate_from_rollup_history`), NOT a
 * NAV-style ratio curve. With the running peak starting near ₹0, dividing by
 * the peak (or by `max(|peak|, 1)`) produces nonsense like -7068% from a
 * ₹70k drawdown. The capital baseline from the basket-wide KPI strip
 * (`kpi.capital_deployed`) is the right denominator — it yields a percentage
 * comparable to `max_drawdown_aggregate` from the backend aggregator. If
 * capital isn't known (legacy rollup row, brand-new basket), we render an
 * empty state rather than a misleading ratio.
 */
import React, { useMemo } from 'react';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer,
  Tooltip as RechartsTooltip, XAxis, YAxis,
} from 'recharts';
import type { MarketplaceEquityPoint } from '../../types/marketplaceAnalytics';

interface Props {
  equityCurve: MarketplaceEquityPoint[];
  capitalDeployed: number | null | undefined;
  height?: number;
}

const MarketplaceDrawdownChart: React.FC<Props> = ({
  equityCurve, capitalDeployed, height = 220,
}) => {
  const data = useMemo(() => {
    if (!equityCurve || equityCurve.length === 0) return [];
    // `!capitalDeployed` already catches 0/null/undefined; an explicit
    // negative check would only matter for malformed input. Cleaner.
    if (!capitalDeployed || capitalDeployed < 0) return [];
    // Clamp the initial peak to be non-negative so a day-1 LOSS registers
    // as a real drawdown rather than the equity-vs-itself zero a naive
    // `peak = equityCurve[0].equity` seed would produce. When day-1 equity
    // is already positive, the loop's `if eq > peak` check below tracks
    // that peak naturally — `Math.max(0, …)` is a no-op on the rising case
    // and only kicks in when equity[0] is negative. Matches the backend
    // NAV-style aggregator's contract that the curve starts at baseline.
    let peak = Math.max(0, equityCurve[0].equity);
    return equityCurve.map((p) => {
      if (p.equity > peak) peak = p.equity;
      const dd = (p.equity - peak) / capitalDeployed;
      return { date: p.date, dd: Math.round(dd * 10000) / 100 };
    });
  }, [equityCurve, capitalDeployed]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-gray-500 dark:text-gray-400">
        No drawdown data yet.
      </div>
    );
  }

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 8 }}>
          <defs>
            <linearGradient id="mp-dd-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.05} />
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0.45} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: 'currentColor' }}
            tickLine={false}
            axisLine={{ stroke: 'rgba(148,163,184,0.4)' }}
            minTickGap={32}
          />
          <YAxis
            tickFormatter={(v: number) => `${v.toFixed(1)}%`}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            tickLine={false}
            axisLine={{ stroke: 'rgba(148,163,184,0.4)' }}
            domain={[(dataMin: number) => Math.min(dataMin, 0), 0]}
            width={60}
          />
          <RechartsTooltip
            contentStyle={{
              fontSize: 12, borderRadius: 6,
              border: '1px solid rgba(148,163,184,0.4)',
              background: 'rgba(255,255,255,0.95)',
            }}
            formatter={(v) => [`${Number(v ?? 0).toFixed(2)}%`, 'Drawdown']}
          />
          <Area
            type="monotone"
            dataKey="dd"
            stroke="#ef4444"
            strokeWidth={1.5}
            fill="url(#mp-dd-grad)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MarketplaceDrawdownChart;
